<script setup>
import {
  ref,
  watch,
  computed,
} from 'vue';
import { useToast } from 'vue-toastification';
import { isIOS } from '@/utils/browser';
import LogoModal from '@/components/modal/LogoModal.vue';
import FormInput from '@/components/form/FormInput.vue';
import FormCheckBox from '@/components/form/FormCheckBox.vue';
import FormSelect from '@/components/form/FormSelect.vue';
import FormTextArea from '@/components/form/FormTextArea.vue';
import FordButton from '@/components/FordButton.vue';

const emit = defineEmits(['validated', 'close']);

const props = defineProps({
  id: String, // eventId?
  title: String,
  subTitle: String,
  submitButtonText: {
    type: String,
    default: 'Submit',
  },
  fleetSizes: {
    type: Array,
    default: () => [],
  }, // [{ text, value }, etc..]
  solutions: {
    type: Array,
    default: () => [],
  }, // [{ checked, id, name }, etc..]
  showQuestion: Boolean,

  // success/error toast message
  success: Object, // { value, message, timeout }
  error: Object, // { value, message, timeout }

  interestedText: String,
  outsideText: String,

  hasNotInterested: {
    type: Boolean,
    default: true,
  },
});

const solutionsModel = ref(props.solutions);

const textInputs = ref({
  firstName: '',
  lastName: '',
  company: '',
  email: '',
  phone: '',
  zip: '',
});

const fleetSizeSelection = ref();

const notInterested = ref({
  checked: false,
  name: 'I am not interested at this time',
});

const notInterestedDisabled = computed(
  () => solutionsModel.value.some((solution) => solution.checked === true),
);

// clear checkboxes if the user is not interested
watch(notInterested.value, (value) => {
  if (value.checked) {
    solutionsModel.value.forEach((solution) => {
      solution.checked = false; // eslint-disable-line
    });
  }
});

const allowContactInterested = ref({
  checked: true,
  text: props.interestedText,
});

const allowContactOutside = ref({
  checked: true,
  text: props.outsideText,
});

const questionForEvent = ref();

const toast = useToast();

const validate = () => {
  const {
    firstName,
    lastName,
    phone,
    email,
    zip,
  } = textInputs.value;

  if (!firstName || !lastName) {
    return { message: 'Please provide values for all required fields', valid: false };
  }

  if ((phone.length > 0) && (phone.replace(/[^0-9.]/g, '').length < 10)) {
    return { message: 'Please provide your full phone number including area code', valid: false };
  }

  if (!email) {
    return { message: 'Please provide values for all required fields', valid: false };
  }

  if (!(email && email.includes('.') && email.includes('@'))) {
    return { message: 'Please provide an email address containing both an at (@) character and a period', valid: false };
  }

  if (!zip) {
    return { message: 'Please provide values for all required fields', valid: false };
  }

  if (zip && zip.length < 5) {
    return { message: 'Please provide at least a 5 digit zip code', valid: false };
  }

  if (!notInterested.value.checked && !solutionsModel.value.some(({ checked }) => checked)) {
    return { message: 'Please provide values for all required fields', valid: false };
  }

  return { valid: true };
};

const mapPayload = () => {
  const {
    firstName,
    lastName,
    company,
    phone,
    email,
    zip,
  } = textInputs.value;

  return {
    eventId: props.id,
    question: questionForEvent.value,
    user: {
      // id: '0',
      email,
      firstName,
      lastName,
      phone,
      companyName: company,
      zip,
      userProfileQuestion: {
        fleetSizeId: fleetSizeSelection.value,
        interestedInFordProSolutions: solutionsModel.value
          .filter((x) => x.checked)
          .map((x) => x.name).join(', '), // api may soon change
        allowContactInterested: allowContactInterested.value.checked,
        allowContactOutside: allowContactOutside.value.checked,
      },
    },
  };
};

const loading = ref(false);
const logo = ref(null);

const submit = () => {
  toast.clear();
  const validation = validate();

  if (!validation.valid && isIOS()) { // accepted workaround for ios toast bug: scroll to the top
    document.activeElement.blur();
    logo.value.$el.scrollTo(0, 0);
    setTimeout(() => {
      toast.error(validation.message, { timeout: 10000 });
    }, 100);
  } else if (!validation.valid) {
    toast.error(validation.message, { timeout: 10000 });
  } else {
    loading.value = true;
    emit('validated', mapPayload());
  }
};

const defaultSucessMessage = 'Congratulations! You are now registered for this event.';

// show success validation toast
watch(
  () => props.success,
  (obj) => {
    if (obj.value) {
      toast.success(
        obj.message || defaultSucessMessage,
        { timeout: 10000 },
      );
    }
  },
  { deep: true },
);

// show error validation toast
watch(
  () => props.error,
  (obj) => {
    if (obj.value) {
      toast.error(
        obj.message || defaultSucessMessage,
        { timeout: 10000 },
      );
    }
  },
  { deep: true },
);
</script>

<template>
  <logo-modal
    ref="logo"
    @close="() => emit('close')"
  >
    <template #body>
      <div
        @keydown.enter="submit"
        class="
          background-wrap
          bg-ford-gray-light
          px-[50px]
          py-[50px]
          tablet-sm:px-[100px]
          tablet-sm:py-[50px]
        "
      >
        <div class="form-wrap max-w-[700px]">
          <h1 class="font-FordAntennaSemiBold text-[24px] mb-[25px]">
            {{ title }}
          </h1>
          <p class="
            text-ford-gray-darker
            font-FordAntennaRegular
            text-[16px]
            mb-[35px]
          ">
            {{ subTitle }}
          </p>
          <form-input
            label="First Name"
            placeholder="First Name"
            v-model="textInputs.firstName"
            required
          />
          <form-input
            label="Last Name"
            placeholder="Last Name"
            v-model="textInputs.lastName"
            required
          />
          <form-input
            label="Company"
            placeholder="Company"
            v-model="textInputs.company"
          />
          <form-input
            label="Email Address"
            v-model="textInputs.email"
            placeholder="email@webaddress.com"
            title="Please provide an email address
               containing both an at (@) character and a period."
            type="email"
            required
          />
          <form-input
            label="Mobile Phone"
            v-model="textInputs.phone"
            name="phone"
            title="Please provide your full phone number including area code."
            placeholder="(000) 000-0000"
            type="tel"
            maxlength="14"
          />
          <form-input
            label="Zip Code"
            v-model="textInputs.zip"
            name="zip"
            placeholder="12345"
            maxlength="10"
            type="zip"
            required
          />

          <form-select
            v-if="fleetSizes.length > 0"
            label="Fleet Size"
            :data="fleetSizes"
            v-model="fleetSizeSelection"
            placeholder="Select your fleet size"
          />

          <h2 class="text-black font-fordAntennaMedium mb-[25px]">
            Select the Ford Pro Solutions you want to learn more about:
            <sup class="text-red-500">*</sup>
          </h2>

          <div class="solutions flex flex-wrap [&>*]:min-w-[25%] mb-[35px]">
            <form-check-box
              v-for="box in solutionsModel"
              v-model="box.checked"
              :disabled="notInterested.checked"
              :name="box.id"
              :id="box.id"
              :label="box.name"
              :key="box.id"
            />
            <form-check-box
              v-if="hasNotInterested"
              class="not-interested"
              name="notInterested"
              :disabled="notInterestedDisabled"
              :label="notInterested.name"
              v-model="notInterested.checked"
            />
          </div>

          <h2 class="text-black font-fordAntennaMedium mb-[25px]">
            Stay Connected
          </h2>

          <form-check-box
            class="contact-me-ford"
            v-model="allowContactInterested.checked"
            :label="allowContactInterested.text"
          />

          <form-check-box
            class="contact-me-affiliates mb-[35px]"
            v-model="allowContactOutside.checked"
            :label="allowContactOutside.text"
          />

          <form-text-area v-if="showQuestion"
            class="question"
            rows="5"
            label="Have a question you'd like answered during the event? Tell us below."
            v-model="questionForEvent"
          />

          <ford-button class="mt-[35px]" @method="submit">
            <img v-if="loading"
              :src="require('@/assets/img/spinner.svg')" alt="spinner"
            />
            <span v-else>{{ submitButtonText }}</span>
          </ford-button>
        </div> <!-- .form-wrap -->
      </div> <!-- .background-wrap -->
    </template>
  </logo-modal>
</template>
